import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FolderKanban, Image, MessageSquare, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';

interface Project {
  id: string;
  name: string;
  description: string | null;
  coverImageUrl: string | null;
  status: string;
  workItemCount: number;
  pendingApprovalCount: number;
  commentCount: number;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export function ProjectsListPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProjects() {
      try {
        setIsLoading(true);
        const response = await api.get<Project[]>('/projects');
        setProjects(Array.isArray(response.data) ? response.data : []);
        setError(null);
      } catch (err) {
        setError('Failed to load projects');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchProjects();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-accent hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-2xl font-bold flex items-center gap-3">
          <div className="p-2 rounded-xl bg-accent/10">
            <FolderKanban className="w-6 h-6 text-accent" />
          </div>
          All Projects
        </h1>
        <p className="text-text-secondary mt-1">
          Browse all your projects
        </p>
      </motion.div>

      {/* Empty state */}
      {projects.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <FolderKanban className="w-16 h-16 mx-auto text-text-muted mb-4" />
          <h2 className="text-xl font-semibold mb-2">No projects yet</h2>
          <p className="text-text-secondary">
            Your projects will appear here once OML Design shares them with you.
          </p>
        </motion.div>
      )}

      {/* Projects grid */}
      {projects.length > 0 && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {projects.map((project) => (
            <motion.div key={project.id} variants={itemVariants}>
              <Link to={`/projects/${project.id}`}>
                <motion.div
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-bg-secondary rounded-2xl border border-border overflow-hidden group cursor-pointer hover:border-accent/30 transition-colors"
                >
                  {/* Cover image */}
                  <div className="relative h-48 overflow-hidden bg-bg-tertiary">
                    {project.coverImageUrl ? (
                      <motion.img
                        src={project.coverImageUrl}
                        alt={project.name}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.4 }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FolderKanban className="w-16 h-16 text-text-muted" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                    {/* Status badge */}
                    <div className="absolute top-3 right-3">
                      {project.status === 'active' ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                          Active
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-text-muted/20 text-text-muted border border-text-muted/30">
                          Completed
                        </span>
                      )}
                    </div>

                    {/* Title overlay */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white text-xl font-semibold group-hover:text-accent-light transition-colors">
                        {project.name}
                      </h3>
                      {project.description && (
                        <p className="text-white/70 text-sm mt-1 line-clamp-1">
                          {project.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm text-text-secondary">
                        <span className="flex items-center gap-1.5">
                          <Image className="w-4 h-4" />
                          {project.workItemCount || 0}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <MessageSquare className="w-4 h-4" />
                          {project.commentCount || 0}
                        </span>
                      </div>

                      {(project.pendingApprovalCount || 0) > 0 ? (
                        <motion.span
                          initial={{ scale: 0.8 }}
                          animate={{ scale: 1 }}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20"
                        >
                          <Clock className="w-3 h-3" />
                          {project.pendingApprovalCount} pending
                        </motion.span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-green-500 text-sm">
                          <CheckCircle2 className="w-4 h-4" />
                          All approved
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
